// Petty cash screens: VAT arithmetic, the checks a receipt line must pass (the backend runs the same
// rules again on save and submit), the ZATCA QR code of a receipt, and the Excel import of lines.
// Pure functions: no API calls, no React.

import { DISPLAY_STATUS } from '../types/pettyCash';
import type {
  AllocationType,
  ApprovalStageView,
  ClaimLineInput,
  DisplayStatus,
  FinanceStageInfo,
  ImprestCheckMode,
  LineCheck,
  LineType,
  PettyCashCategory,
  PettyCashProcessSettings,
  PettyCashSettings,
  QrData,
  ReimburseAccountSetting,
  ReimburseMethod,
  VatCodeOption,
} from '../types/pettyCash';
import { MAPPED_REIMBURSE_METHODS } from '../types/pettyCash';
import { fixTwoDigitYear, isDocumentDate } from './documentDate';

// ---------------------------------------------------------------- numbers and dates

/** Rounded to 2 decimals (halfway away from zero, as SAP rounds amounts). */
export const round2 = (n: number): number => {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.sign(v) * Math.round((Math.abs(v) + Number.EPSILON) * 100) / 100;
};

/** VAT on an amount before VAT: amount x rate %, rounded to 2 decimals. */
export const vatOf = (amount: number, rate: number): number => round2((Number(amount) || 0) * (Number(rate) || 0) / 100);

/** The typed VAT is within the tolerance of amount x rate. */
export const vatWithin = (amount: number, rate: number, vat: number, tolerance: number): boolean =>
  Math.abs(round2(vat) - vatOf(amount, rate)) <= (Number(tolerance) || 0) + 1e-9;

const amountFormat = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** 1234.5 -> "1,234.50"; empty for null. */
export const formatAmount = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(Number(n)) ? '' : amountFormat.format(Number(n));

/** "1,234.50 SAR" */
export const formatMoney = (n: number | null | undefined, currency?: string | null): string => {
  const v = formatAmount(n);
  return v && currency ? `${v} ${currency}` : v;
};

/** A typed amount ("1,234.50", " 12 ") as a number; null when it is not one. */
export function parseAmount(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  const s = String(raw ?? '').replace(/[,\s]/g, '').replace(/(SAR|SR|KWD|AED|ر\.س)$/i, '');
  if (!s || !/^-?\d*\.?\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * 'YYYY-MM-DD' -> 'dd/mm/yyyy'. A timestamp with a time zone (the server's ISO times are UTC) shows the
 * browser's local date, so a claim submitted at 01:00 in Riyadh is not dated the day before.
 */
export function formatDate(value: string | null | undefined): string {
  const s = String(value ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(s) && /(Z|[+-]\d{2}:?\d{2})$/i.test(s)) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return formatDate(todayIso(d));
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/** A timestamp for history lists, in the browser's locale. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** Today in the browser's time zone, 'YYYY-MM-DD'. */
export function todayIso(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** Whole days from one 'YYYY-MM-DD' date to another (positive when `to` is later). */
export function daysBetween(from: string, to: string): number {
  const a = Date.UTC(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1, Number(from.slice(8, 10)));
  const b = Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10)));
  return Math.round((b - a) / 86400000);
}

/** A date from a date box: a 2-digit year (0026-09-28) becomes 20yy; '' when it is not a usable date. */
export function cleanDateInput(value: unknown): string {
  const fixed = fixTwoDigitYear(value);
  return isDocumentDate(fixed) ? fixed : String(value ?? '').trim();
}

// ---------------------------------------------------------------- VAT numbers

const VAT_RULES: Record<string, { label: string; rule: string; pattern: RegExp }> = {
  SA: { label: 'Saudi Arabia', rule: '15 digits, first and last digit 3', pattern: /^3\d{13}3$/ },
  AE: { label: 'United Arab Emirates', rule: '15 digits starting with 100', pattern: /^100\d{12}$/ },
};

export const vatDigits = (raw: unknown): string => String(raw ?? '').replace(/\D/g, '');

/** The same check as the backend (utils/vatNumber.ts): digits only, the country's format. */
export function checkVatFormat(raw: unknown, country: string | null | undefined): { digits: string; ok: boolean; message: string | null } {
  const text = String(raw ?? '').trim();
  const digits = vatDigits(text);
  if (!text) return { digits, ok: false, message: 'The VAT number is missing' };
  if (/[^\d\s-]/.test(text)) return { digits, ok: false, message: 'A VAT number has digits only' };
  if (/^0+$/.test(digits)) return { digits, ok: false, message: 'The VAT number is a placeholder of zeros' };
  const rule = VAT_RULES[String(country || '').toUpperCase()];
  if (rule) {
    if (!rule.pattern.test(digits)) {
      return { digits, ok: false, message: `${rule.label} VAT number: ${rule.rule}; this one has ${digits.length} digit${digits.length === 1 ? '' : 's'}` };
    }
    return { digits, ok: true, message: null };
  }
  if (digits.length < 5 || digits.length > 20) return { digits, ok: false, message: 'A VAT number has 5 to 20 digits' };
  return { digits, ok: true, message: null };
}

/** True once the typed number is long enough to look it up in SAP. */
export function vatReadyForLookup(raw: unknown, country: string | null | undefined): boolean {
  return checkVatFormat(raw, country).ok;
}

// ---------------------------------------------------------------- ZATCA QR code

function utf8(bytes: number[]): string {
  try {
    return decodeURIComponent(bytes.map((b) => `%${b.toString(16).padStart(2, '0')}`).join(''));
  } catch {
    return String.fromCharCode(...bytes);
  }
}

/**
 * The fields of a ZATCA QR code (base64 TLV): 1 seller name, 2 VAT number, 3 time stamp, 4 total with
 * VAT, 5 VAT. Phase-2 codes add tags 6 to 9 (hash, signature, key, stamp), which are skipped. Null when
 * the text is not such a code.
 */
export function parseZatcaQr(text: string | null | undefined): QrData | null {
  const clean = String(text ?? '').replace(/\s+/g, '');
  if (!clean || !/^[A-Za-z0-9+/]+=*$/.test(clean)) return null;
  let bin: string;
  try {
    bin = atob(clean);
  } catch {
    return null;
  }
  const bytes = Array.from(bin, (c) => c.charCodeAt(0));
  const out: QrData = {};
  let i = 0;
  while (i + 2 <= bytes.length) {
    const tag = bytes[i];
    const len = bytes[i + 1];
    if (!tag || i + 2 + len > bytes.length) break;
    if (tag <= 5) {
      const value = utf8(bytes.slice(i + 2, i + 2 + len)).trim();
      if (tag === 1) out.seller = value;
      else if (tag === 2) out.vatNumber = value;
      else if (tag === 3) out.timestamp = value;
      else if (tag === 4) out.totalWithVat = value;
      else if (tag === 5) out.vatTotal = value;
    }
    i += 2 + len;
  }
  return out.vatNumber || out.totalWithVat ? out : null;
}

/** The date of a QR time stamp ('2026-10-26T20:30:47', '2026-10-26 20:30', '26/10/2026 ...') as 'YYYY-MM-DD'. */
export function qrDate(timestamp: string | null | undefined): string | null {
  const s = String(timestamp ?? '').trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/.exec(s);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  return null;
}

/** What a QR code fills on a VAT line: VAT number, date, total, VAT and the amount before VAT. */
export function linePartFromQr(qr: QrData): Partial<ClaimLineInput> {
  const part: Partial<ClaimLineInput> = {};
  const digits = vatDigits(qr.vatNumber);
  if (digits) part.supplierVat = digits;
  const date = qrDate(qr.timestamp);
  if (date && isDocumentDate(date)) part.invoiceDate = date;
  const total = parseAmount(qr.totalWithVat);
  const vat = parseAmount(qr.vatTotal);
  if (total !== null) part.total = round2(total);
  if (vat !== null) part.vatAmount = round2(vat);
  if (total !== null && vat !== null) part.amount = round2(total - vat);
  return part;
}

// ---------------------------------------------------------------- lines

export interface LineContext {
  settings: PettyCashSettings;
  vatRegistered: boolean;
  vatCountry: string | null;
  categories: PettyCashCategory[];
  vatCodes: VatCodeOption[];
  /** 'YYYY-MM-DD' */
  today: string;
  /** A receipt image is attached or chosen. */
  hasImage: boolean;
}

/** The VAT rate of a VAT code (0 when unknown). */
export const rateOf = (code: string | null | undefined, codes: VatCodeOption[]): number =>
  codes.find((c) => c.code === code)?.rate ?? 0;

/** A new, empty line of the given type with the company's default VAT code. */
export function emptyLine(lineNum: number, lineType: LineType, settings: PettyCashSettings, codes: VatCodeOption[], vatRegistered: boolean): ClaimLineInput {
  const vatCode = lineType === 'VAT' ? settings.vatCode : (vatRegistered ? settings.nonVatCode : null);
  return {
    lineNum,
    lineType,
    cardCode: null,
    supplierName: '',
    supplierVat: null,
    invoiceNo: '',
    invoiceDate: '',
    amount: 0,
    vatRate: lineType === 'VAT' ? rateOf(vatCode, codes) : 0,
    vatAmount: 0,
    total: 0,
    vatCode: vatCode || null,
    categoryId: null,
    description: null,
    buyerNamed: false,
    qr: null,
  };
}

/**
 * What a category says about a line's G/L account (display only; the server decides). When the category
 * names an SAP item or item class for the claim's allocation, SAP's Advanced G/L rules decide (by the
 * project's warehouse, or the department warehouse of the setup), as for a purchase through PR -> PO ->
 * receipt; its fixed account is used when the rules give none, or when it names neither.
 */
export function accountForCategory(
  category: PettyCashCategory | null | undefined,
  allocationType: AllocationType,
): { code: string | null; name: string | null; ruleBasis: string | null; auxiliary: string | null; auxiliaryName: string | null } {
  if (!category) return { code: null, name: null, ruleBasis: null, auxiliary: null, auxiliaryName: null };
  const project = allocationType === 'Project';
  const item = String((project ? category.projectItem : category.deptItem) || '').trim();
  const itemName = String((project ? category.projectItemName : category.deptItemName) || '').trim();
  const itemClass = String((project ? category.projectClass : category.deptClass) || '').trim();
  const ruleBasis = item ? `item ${item}${itemName ? ` ${itemName}` : ''}` : itemClass ? `class ${itemClass}` : null;
  // Each side's auxiliary; an older server sends one default for both in Dimension 5 of the codes.
  const ownAux = String((project ? category.projectAuxiliary : category.deptAuxiliary) || '').trim();
  const auxiliary = ownAux || String(category.dims?.['5'] || '').trim() || null;
  const auxiliaryName = ownAux ? ((project ? category.projectAuxiliaryName : category.deptAuxiliaryName) ?? null) : null;
  return project
    ? { code: category.projectAccount || null, name: category.projectAccountName ?? null, ruleBasis, auxiliary, auxiliaryName }
    : { code: category.costCenterAccount || null, name: category.costCenterAccountName ?? null, ruleBasis, auxiliary, auxiliaryName };
}

/** The holder's hint for a category's account and auxiliary: "Posts to 511010128 Utilities (Direct), auxiliary UT0001"; null when it has none. */
export function categoryAccountText(hint: { code: string | null; name: string | null; ruleBasis: string | null; auxiliary?: string | null; auxiliaryName?: string | null }): string | null {
  const fixed = hint.code ? `${hint.code}${hint.name ? ` ${hint.name}` : ''}` : '';
  const aux = hint.auxiliary ? `auxiliary ${hint.auxiliary}${hint.auxiliaryName ? ` ${hint.auxiliaryName}` : ''}` : '';
  if (hint.ruleBasis) return `Account from SAP's G/L rules (${hint.ruleBasis})${fixed ? `, else ${fixed}` : ''}${aux ? `; ${aux}` : ''}`;
  if (fixed) return `Posts to ${fixed}${aux ? `, ${aux}` : ''}`;
  return aux ? `Auxiliary ${aux.slice('auxiliary '.length)}` : null;
}

/** One line under a category in the receipt's list: the account and auxiliary it gives this claim's side. */
export function categoryDetail(category: PettyCashCategory, allocationType: AllocationType): string {
  const h = accountForCategory(category, allocationType);
  const account = h.ruleBasis
    ? `SAP's G/L rules (${h.ruleBasis})${h.code ? `, else ${h.code}` : ''}`
    : h.code ? `${h.code}${h.name ? ` ${h.name}` : ''}` : 'No account for this side yet';
  return `${account}${h.auxiliary ? ` · ${h.auxiliary}${h.auxiliaryName ? ` ${h.auxiliaryName}` : ''}` : ''}`;
}

/**
 * The category gives this side (project or department) an account: an SAP item or item class (SAP's G/L rules) or a
 * fixed account. The receipt's list offers only those: a category with one side (Audit fees: departments only)
 * would otherwise show on the other side with no account, and the receipt could not be saved.
 */
export function categoryHasSide(category: PettyCashCategory, allocationType: AllocationType): boolean {
  const project = allocationType === 'Project';
  const values = project
    ? [category.projectItem, category.projectClass, category.projectAccount]
    : [category.deptItem, category.deptClass, category.costCenterAccount];
  return values.some((v) => String(v || '').trim() !== '');
}

/** Petty Cash Setup's category search: every word typed is in the name, or either side's item, class, account or auxiliary. */
export function categoryListMatches(category: PettyCashCategory, query: string): boolean {
  const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const text = [category.name, categoryDetail(category, 'Project'), categoryDetail(category, 'CostCenter'),
    category.projectItem, category.projectClass, category.deptItem, category.deptClass].filter(Boolean).join(' ').toLowerCase();
  return words.every((w) => text.includes(w));
}

/** Every word typed is in the category's name, or in the account or auxiliary it gives this side (any case). */
export function categoryMatches(category: PettyCashCategory, allocationType: AllocationType, query: string): boolean {
  const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const text = `${category.name} ${categoryDetail(category, allocationType)}`.toLowerCase();
  return words.every((w) => text.includes(w));
}

const err = (code: string, message: string): LineCheck => ({ level: 'error', code, message });
const warn = (code: string, message: string): LineCheck => ({ level: 'warning', code, message });

/** What is wrong with a line (errors block saving the receipt; warnings only inform). */
export function validateLine(line: ClaimLineInput, ctx: LineContext): LineCheck[] {
  const out: LineCheck[] = [];
  const vatLine = line.lineType === 'VAT';
  const { settings } = ctx;
  if (vatLine && !ctx.vatRegistered) out.push(err('vat-not-used', 'This company is not VAT-registered: record the receipt as a non-VAT receipt'));
  if (vatLine) {
    const fmt = checkVatFormat(line.supplierVat, ctx.vatCountry);
    if (!fmt.ok) out.push(err('vat-number', fmt.message || 'The VAT number is not valid'));
    if (!line.cardCode) out.push(err('supplier', 'Find the supplier in SAP by its VAT number'));
    if (!line.vatCode) out.push(err('vat-code', 'Choose the VAT code'));
  } else if (!String(line.supplierName || '').trim()) {
    out.push(err('supplier', 'Type the supplier name'));
  }
  if (!String(line.invoiceNo || '').trim()) out.push(err('invoice-no', vatLine ? 'Type the tax invoice number' : 'Type the invoice number'));
  if (!isDocumentDate(line.invoiceDate)) {
    out.push(err('invoice-date', 'Enter the invoice date'));
  } else if (line.invoiceDate > ctx.today) {
    out.push(err('invoice-date', 'The invoice date is in the future'));
  } else if (settings.maxReceiptAgeDays > 0 && daysBetween(line.invoiceDate, ctx.today) > settings.maxReceiptAgeDays) {
    out.push(err('invoice-age', `The receipt is older than ${settings.maxReceiptAgeDays} days`));
  }
  const amount = Number(line.amount) || 0;
  const vat = Number(line.vatAmount) || 0;
  const total = Number(line.total) || 0;
  if (!(amount > 0)) out.push(err('amount', vatLine ? 'Enter the amount before VAT' : 'Enter the invoice amount'));
  if (vatLine) {
    if (amount > 0 && !vatWithin(amount, line.vatRate, vat, settings.vatTolerance)) {
      out.push(err('vat-amount', `The VAT should be ${formatAmount(vatOf(amount, line.vatRate))} (${formatAmount(amount)} x ${line.vatRate}%)`));
    }
  } else if (Math.abs(vat) > 0.004) {
    out.push(err('vat-amount', 'A non-VAT receipt has no VAT amount'));
  }
  if (amount > 0 && Math.abs(total - round2(amount + vat)) > 0.005) {
    out.push(err('total', vatLine ? 'The total must equal the amount before VAT plus the VAT' : 'The total must equal the invoice amount'));
  }
  const category = ctx.categories.find((c) => c.categoryId === line.categoryId) || null;
  if (!line.categoryId) out.push(err('category', 'Choose the expense category'));
  else if (!category) out.push(err('category', 'This expense category is no longer available; choose another'));
  else if (vatLine && !category.vatDeductible) {
    // The server refuses to submit it (checkLine 'vat-not-deductible'); nothing turns it into a non-VAT receipt.
    out.push(err('vat-not-deductible', `VAT on "${category.name}" cannot be claimed: record this receipt as a non-VAT receipt`));
  }
  if (vatLine && settings.requireBuyerNamed && !line.buyerNamed) {
    out.push(err('buyer-named', 'Confirm that the invoice names the company and its VAT number, or record it as a non-VAT receipt'));
  }
  if (settings.requireImage && !ctx.hasImage) out.push(err('image', 'Attach the receipt image'));
  if (settings.maxReceiptAmount && total > settings.maxReceiptAmount) {
    const text = `Above the ${formatAmount(settings.maxReceiptAmount)} limit for one receipt: such purchases go through a PR and PO`;
    out.push(settings.blockOverLimit ? err('limit', text) : warn('limit', text));
  }
  return out;
}

export const hasErrors = (checks: LineCheck[] | null | undefined): boolean => (checks || []).some((c) => c.level === 'error');

/** Totals of the lines (disallowed lines left out). */
export function lineTotals(lines: Array<{ amount: number; vatAmount: number; total: number; status?: string }>): { beforeVat: number; vat: number; total: number } {
  let beforeVat = 0;
  let vat = 0;
  let total = 0;
  for (const l of lines) {
    if (l.status === 'Disallowed') continue;
    beforeVat += Number(l.amount) || 0;
    vat += Number(l.vatAmount) || 0;
    total += Number(l.total) || 0;
  }
  return { beforeVat: round2(beforeVat), vat: round2(vat), total: round2(total) };
}

// ---------------------------------------------------------------- imprest, reimbursement, stages, quarters (Oct 2026)

/** The settings' defaults (the backend's column defaults), used for any setting an older server leaves out. */
export const PROCESS_SETTING_DEFAULTS: PettyCashProcessSettings = {
  imprestCheck: 'warn',
  imprestTolerance: 1,
  reimburseTargetDays: 7,
  weekendDays: [5, 6],
  reimbursePost: false,
  reimburseBankAccount: null,
  reimburseBankAccountName: null,
  reimburseAccounts: {},
  quarterlyConfirm: true,
  confirmWindowDays: 15,
  requireOriginals: false,
  financeStages: [],
};

const text = (v: unknown): string => String(v ?? '').trim();

/** The credit accounts as the server sends them: Cash, Cheque and Other only, each with an account. */
export function reimburseAccountList(raw: unknown): Partial<Record<ReimburseMethod, ReimburseAccountSetting>> {
  const out: Partial<Record<ReimburseMethod, ReimburseAccountSetting>> = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const method of MAPPED_REIMBURSE_METHODS) {
    const v: any = (raw as any)[method];
    const account = text(v?.account);
    if (!account) continue;
    out[method] = { account, accountName: text(v?.accountName) || null, auxiliary: text(v?.auxiliary) || null, auxiliaryName: text(v?.auxiliaryName) || null };
  }
  return out;
}

/** The account a top-up credits for this way of paying: its own, else the bank account (null: neither is set). As the backend. */
export function reimburseCredit(
  s: Pick<PettyCashProcessSettings, 'reimburseBankAccount' | 'reimburseBankAccountName' | 'reimburseAccounts'>,
  method: ReimburseMethod
): ReimburseAccountSetting | null {
  const own = MAPPED_REIMBURSE_METHODS.includes(method) ? s.reimburseAccounts?.[method] : undefined;
  if (own && text(own.account)) return { ...own, account: text(own.account), auxiliary: text(own.auxiliary) || null };
  const bank = text(s.reimburseBankAccount);
  return bank ? { account: bank, accountName: s.reimburseBankAccountName ?? null, auxiliary: null, auxiliaryName: null } : null;
}

/** The top-up would credit the box it debits (the same account and auxiliary): the backend refuses it. */
export function isSameBox(debit: { account: unknown; auxiliary: unknown }, credit: { account: unknown; auxiliary: unknown }): boolean {
  const up = (v: unknown) => text(v).toUpperCase();
  return up(debit.account) !== '' && up(debit.account) === up(credit.account) && up(debit.auxiliary) === up(credit.auxiliary);
}

/** `123020100 Petty Cash Procurement with auxiliary PC00001 Head Office`, the names when known. */
export function creditText(c: { account: string; accountName?: string | null; auxiliary?: string | null; auxiliaryName?: string | null }): string {
  const account = `${text(c.account)}${text(c.accountName) ? ` ${text(c.accountName)}` : ''}`;
  return text(c.auxiliary) ? `${account} with auxiliary ${text(c.auxiliary)}${text(c.auxiliaryName) ? ` ${text(c.auxiliaryName)}` : ''}` : account;
}

const finiteOr = (v: unknown, fallback: number): number => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? fallback : Number(v));

/** Weekend days as numbers 0..6 (from an array, or a '5,6' text), each once, in order. */
export function weekendDayList(raw: unknown, fallback: number[] = PROCESS_SETTING_DEFAULTS.weekendDays): number[] {
  const parts = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(',') : null;
  if (!parts) return [...fallback];
  const days = parts.map((p) => Number(String(p).trim())).filter((n) => Number.isInteger(n) && n >= 0 && n <= 6);
  return Array.from(new Set(days)).sort((a, b) => a - b);
}

/** The imprest, reimbursement, confirmation, originals and finance stage settings, with defaults for those missing. */
export function processSettings(s: Partial<PettyCashProcessSettings> | null | undefined): PettyCashProcessSettings {
  const d = PROCESS_SETTING_DEFAULTS;
  const v: Partial<PettyCashProcessSettings> = s || {};
  const mode = v.imprestCheck;
  return {
    imprestCheck: mode === 'off' || mode === 'warn' || mode === 'block' ? mode : d.imprestCheck,
    imprestTolerance: finiteOr(v.imprestTolerance, d.imprestTolerance),
    reimburseTargetDays: finiteOr(v.reimburseTargetDays, d.reimburseTargetDays),
    weekendDays: v.weekendDays === undefined || v.weekendDays === null ? [...d.weekendDays] : weekendDayList(v.weekendDays),
    reimbursePost: typeof v.reimbursePost === 'boolean' ? v.reimbursePost : d.reimbursePost,
    reimburseBankAccount: v.reimburseBankAccount ?? null,
    reimburseBankAccountName: v.reimburseBankAccountName ?? null,
    reimburseAccounts: reimburseAccountList(v.reimburseAccounts),
    quarterlyConfirm: typeof v.quarterlyConfirm === 'boolean' ? v.quarterlyConfirm : d.quarterlyConfirm,
    confirmWindowDays: finiteOr(v.confirmWindowDays, d.confirmWindowDays),
    requireOriginals: typeof v.requireOriginals === 'boolean' ? v.requireOriginals : d.requireOriginals,
    financeStages: Array.isArray(v.financeStages)
      ? v.financeStages.map((st) => ({ name: String(st?.name ?? ''), role: String(st?.role ?? '') }))
      : [],
  };
}

/** The days of the week (0 = Sunday, as JavaScript's getUTCDay). */
export const WEEKDAYS: Array<{ day: number; short: string; name: string }> = [
  { day: 0, short: 'Sun', name: 'Sunday' },
  { day: 1, short: 'Mon', name: 'Monday' },
  { day: 2, short: 'Tue', name: 'Tuesday' },
  { day: 3, short: 'Wed', name: 'Wednesday' },
  { day: 4, short: 'Thu', name: 'Thursday' },
  { day: 5, short: 'Fri', name: 'Friday' },
  { day: 6, short: 'Sat', name: 'Saturday' },
];

/** The chip of a claim status (ClaimStatus or Reimbursed); an unknown status shows as it is. */
export function statusLook(status: string | null | undefined): (typeof DISPLAY_STATUS)[DisplayStatus] {
  const key = String(status || '') as DisplayStatus;
  return DISPLAY_STATUS[key] || { label: String(status || ''), color: 'default' };
}

/** A typed amount that may be left empty: null when empty; ok false when it is not an amount of 0 or more. */
export function parseOptionalAmount(raw: unknown): { value: number | null; ok: boolean } {
  if (String(raw ?? '').trim() === '') return { value: null, ok: true };
  const n = parseAmount(raw);
  return n === null || n < 0 ? { value: null, ok: false } : { value: round2(n), ok: true };
}

/** A number for an amount box: '' for null. */
export const amountInput = (n: number | null | undefined): string => (n === null || n === undefined || !Number.isFinite(Number(n)) ? '' : String(n));

/** A difference with its sign: "+12.00", "-12.00" (a true minus sign), "0.00". */
export function signedAmount(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '';
  const v = round2(Number(n));
  if (v === 0) return formatAmount(0);
  return `${v > 0 ? '+' : '−'}${formatAmount(Math.abs(v))}`;
}

export interface ImprestFigureInput {
  floatAmount: number | null;
  /** The holder's other claims not yet reimbursed. */
  outstanding: number;
  thisClaim: number;
  advances: number | null;
  cashInHand: number | null;
  tolerance: number;
  mode: ImprestCheckMode;
}

/**
 * The imprest check, as the backend computes it (utils/pettyCash.util.ts imprestFigures): expected cash = float -
 * claims not yet reimbursed - this claim - advances (null without a float); difference = cash in hand - expected.
 * Checks (none when the check is off or there is no float), warnings or, in block mode, errors: more claimed than the
 * float, no cash counted, a difference above the tolerance.
 */
export function imprestFigures(i: ImprestFigureInput): { expectedCash: number | null; difference: number | null; checks: LineCheck[] } {
  const float = i.floatAmount === null || i.floatAmount === undefined || !Number.isFinite(Number(i.floatAmount)) ? null : Number(i.floatAmount);
  const outstanding = Number(i.outstanding) || 0;
  const thisClaim = Number(i.thisClaim) || 0;
  const expectedCash = float === null ? null : round2(float - outstanding - thisClaim - (Number(i.advances) || 0));
  const cash = i.cashInHand === null || i.cashInHand === undefined || !Number.isFinite(Number(i.cashInHand)) ? null : Number(i.cashInHand);
  const difference = expectedCash === null || cash === null ? null : round2(cash - expectedCash);
  const checks: LineCheck[] = [];
  if (i.mode !== 'off' && float !== null) {
    const level: LineCheck['level'] = i.mode === 'block' ? 'error' : 'warning';
    const tolerance = Math.max(0, Number(i.tolerance) || 0);
    const claimed = round2(outstanding + thisClaim);
    if (claimed > float + tolerance + 1e-9) {
      checks.push({
        level,
        code: 'imprest-over-float',
        message: `This claim and the claims not yet reimbursed come to ${formatAmount(claimed)}, more than the float of ${formatAmount(float)}`,
      });
    }
    if (cash === null) {
      checks.push({ level, code: 'imprest-no-cash', message: 'Count the cash in hand and type it in the imprest check' });
    } else if (difference !== null && Math.abs(difference) > tolerance + 1e-9) {
      checks.push({
        level,
        code: 'imprest-difference',
        message: `The cash in hand is ${formatAmount(Math.abs(difference))} ${difference < 0 ? 'less' : 'more'} than the expected cash (${formatAmount(tolerance)} allowed): explain it in the note`,
      });
    }
  }
  return { expectedCash, difference, checks };
}

/** The total of the receipts SAP paid: the reimbursement's default amount, and the most it can be. */
export function paidTotal(lines: Array<{ total: number; status?: string | null }>): number {
  return round2(lines.reduce((sum, l) => sum + (l.status === 'Paid' ? Number(l.total) || 0 : 0), 0));
}

/** True when a reimbursement is more than the receipts SAP paid (by a cent or more); the backend refuses it. */
export function aboveReceiptsPaid(amount: number | null | undefined, paid: number): boolean {
  return amount !== null && amount !== undefined && Number.isFinite(amount) && round2(amount) > round2(paid) + 0.004;
}

/**
 * Where a claim stands among its finance stages: its position (1-based) and count, the stage's name, and whether it is
 * the last one (the one that approves the posting). The approval path's Finance stages decide the position; a server
 * without `finance` has one finance stage, as before.
 */
export function financeStageOf(
  finance: FinanceStageInfo | null | undefined,
  stages: ApprovalStageView[] | null | undefined,
): { position: number | null; count: number; name: string | null; isLast: boolean; previousName: string | null; nextName: string | null } {
  const fin = (stages || []).filter((s) => s.kind === 'Finance');
  const count = Math.max(1, Number(finance?.stageCount) || 0, fin.length);
  const current = fin.findIndex((s) => s.status === 'Current');
  const position = current >= 0 ? current + 1 : finance?.isLastStage ? count : null;
  const isLast = finance ? finance.isLastStage !== false : position === null || position >= count;
  const name = finance?.stageName || (current >= 0 ? fin[current].name : null);
  return {
    position,
    count,
    name,
    isLast,
    previousName: current > 0 ? fin[current - 1].name : null,
    nextName: current >= 0 && current + 1 < fin.length ? fin[current + 1].name : null,
  };
}

/** '2026-10-30' -> '2026-Q4' ('' for anything else). */
export function quarterOf(dateIso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})/.exec(String(dateIso ?? ''));
  if (!m || Number(m[2]) < 1 || Number(m[2]) > 12) return '';
  return `${m[1]}-Q${Math.floor((Number(m[2]) - 1) / 3) + 1}`;
}

/** The quarter of `todayIso` and the `count - 1` before it, newest first: ['2026-Q4', '2026-Q3', ...]. */
export function recentQuarters(today: string, count: number): string[] {
  const first = /^(\d{4})-Q([1-4])$/.exec(quarterOf(today));
  if (!first) return [];
  let year = Number(first[1]);
  let q = Number(first[2]);
  const out: string[] = [];
  for (let i = 0; i < count; i += 1) {
    out.push(`${year}-Q${q}`);
    q -= 1;
    if (q === 0) { q = 4; year -= 1; }
  }
  return out;
}

/** The last day of a quarter: '2026-Q3' -> '2026-09-30' ('' for anything else). */
export function quarterEnd(quarter: string | null | undefined): string {
  const m = /^(\d{4})-Q([1-4])$/.exec(String(quarter ?? ''));
  if (!m) return '';
  return `${m[1]}-${['03-31', '06-30', '09-30', '12-31'][Number(m[2]) - 1]}`;
}

/**
 * The quarter whose confirmation is due on `today`: the current one, or the previous one while its window (the days
 * after its end) is still open, as the backend decides it.
 */
export function openQuarter(today: string, windowDays: number): string {
  const [current, previous] = recentQuarters(today, 2);
  if (!previous) return current || '';
  const sincePreviousEnd = daysBetween(quarterEnd(previous), today.slice(0, 10));
  return sincePreviousEnd >= 0 && sincePreviousEnd <= Math.max(0, Number(windowDays) || 0) ? previous : current;
}

const QUARTER_MONTHS = ['Jan to Mar', 'Apr to Jun', 'Jul to Sep', 'Oct to Dec'];

/** '2026-Q4' -> 'Q4 2026 (Oct to Dec)'; anything else as it is. */
export function quarterLabel(quarter: string | null | undefined): string {
  const m = /^(\d{4})-Q([1-4])$/.exec(String(quarter ?? ''));
  return m ? `Q${m[2]} ${m[1]} (${QUARTER_MONTHS[Number(m[2]) - 1]})` : String(quarter ?? '');
}

/** The file name of a download: Content-Disposition's filename* (UTF-8) or filename, else the fallback. */
export function fileNameFromDisposition(header: unknown, fallback: string): string {
  const text = String(header ?? '');
  const star = /filename\*\s*=\s*(?:UTF-8|utf-8)?''([^;]+)/.exec(text);
  if (star) {
    try {
      const name = decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
      if (name) return name;
    } catch {
      // fall through to the plain filename
    }
  }
  const plain = /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i.exec(text);
  const name = (plain?.[1] || plain?.[2] || '').trim();
  return name || fallback;
}

// ---------------------------------------------------------------- Excel import

export interface ImportColumn { key: ImportField; header: string; note: string; aliases: string[] }
export type ImportField =
  | 'type' | 'vat' | 'supplier' | 'vatInvoice' | 'invoice' | 'date' | 'amount' | 'vatAmount' | 'total' | 'category' | 'description';

export const IMPORT_COLUMNS: ImportColumn[] = [
  { key: 'type', header: 'Type (VAT / Non-VAT)', note: 'VAT for a tax invoice from a VAT-registered supplier, Non-VAT otherwise. Left empty: VAT when a VAT invoice no. is given.', aliases: ['type', 'line type', 'receipt type'] },
  { key: 'vat', header: 'Supplier VAT number', note: 'VAT receipts: the supplier\'s VAT registration number (15 digits in Saudi Arabia), or its SAP vendor code. PRPO finds the supplier in SAP with it.', aliases: ['supplier vat number', 'vat number', 'vat no', 'supplier vat', 'supplier#', 'supplier code', 'supplier no'] },
  { key: 'supplier', header: 'Supplier name', note: 'Non-VAT receipts: the shop\'s name as on the receipt. VAT receipts: optional (SAP\'s name is used).', aliases: ['supplier name', 'supplier', 'shop', 'shop name', 'remarks', 'name'] },
  { key: 'vatInvoice', header: 'VAT invoice no.', note: 'VAT receipts: the tax invoice number.', aliases: ['vat invoice no', 'vat invoice#', 'vat invoice', 'tax invoice no', 'tax invoice'] },
  { key: 'invoice', header: 'Normal invoice no.', note: 'Non-VAT receipts: the invoice or receipt number.', aliases: ['normal invoice no', 'normal invoice', 'invoice no', 'invoice number', 'receipt no'] },
  { key: 'date', header: 'Invoice date', note: 'The date on the receipt, e.g. 26/10/2026.', aliases: ['invoice date', 'date', 'receipt date'] },
  { key: 'amount', header: 'Amount before VAT', note: 'VAT receipts: the amount before VAT. Can be left empty when the total is given.', aliases: ['amount before vat', 'before vat', 'net amount'] },
  { key: 'vatAmount', header: 'VAT amount', note: 'VAT receipts: the VAT on the invoice. Left empty: amount x the VAT rate.', aliases: ['vat amount', 'vat'] },
  { key: 'total', header: 'Total', note: 'The total paid: including VAT for a VAT receipt, the invoice amount for a non-VAT one.', aliases: ['total', 'amount', 'total amount', 'total including vat'] },
  { key: 'category', header: 'Expense category', note: 'An expense category as named in Petty Cash Setup.', aliases: ['expense category', 'category'] },
  { key: 'description', header: 'Description', note: 'What was bought (optional).', aliases: ['description', 'details', 'item'] },
];

const normalizeHeader = (h: unknown): string => String(h ?? '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/[.:]+$/, '');

export interface ImportRow {
  /** The row in the sheet, 1-based, header row included. */
  rowNumber: number;
  line: ClaimLineInput;
  /** The category as typed (matched to the setup by name). */
  categoryText: string;
  /** VAT rows: how to find the SAP vendor (by VAT number, or by vendor code when the cell is not a VAT number). */
  vendorBy: 'vat' | 'code' | null;
  vendorKey: string;
  errors: string[];
}

/** An Excel serial day number or a typed date as 'YYYY-MM-DD' (day first: 26/10/2026); null when it is not a date. */
export function parseSheetDate(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 20000 && value < 80000) {
    const ms = Date.UTC(1899, 11, 30) + Math.round(value) * 86400000;
    return new Date(ms).toISOString().slice(0, 10);
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${value.getFullYear()}-${p(value.getMonth() + 1)}-${p(value.getDate())}`;
  }
  const s = String(value ?? '').trim();
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(s);
  if (m) {
    const year = m[3].length === 2 ? `20${m[3]}` : m[3];
    const day = Number(m[1]);
    const month = Number(m[2]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  return null;
}

/**
 * Lines from a sheet (rows of cells). The first row of column names decides which column is which
 * (the template's names, or the user's sheet: Supplier#, VAT INVOICE#, Normal Invoice, Remarks,
 * Amount). Vendors are not looked up here: VAT rows carry the VAT digits for the dialog to check.
 */
export function parseImportTable(
  table: unknown[][],
  ctx: { settings: PettyCashSettings; vatRegistered: boolean; vatCountry: string | null; categories: PettyCashCategory[]; vatCodes: VatCodeOption[]; firstLineNum: number },
): { rows: ImportRow[]; missingHeader: boolean } {
  const rows = (table || []).map((r) => (Array.isArray(r) ? r : []));
  const headerIndex = rows.findIndex((r) => r.some((c) => String(c ?? '').trim() !== ''));
  if (headerIndex < 0) return { rows: [], missingHeader: true };
  const header = rows[headerIndex].map(normalizeHeader);
  const fieldAt = new Map<ImportField, number>();
  // Exact names first, then aliases, so "VAT amount" is not taken for "VAT number".
  for (const col of IMPORT_COLUMNS) {
    const i = header.indexOf(normalizeHeader(col.header));
    if (i >= 0) fieldAt.set(col.key, i);
  }
  for (const col of IMPORT_COLUMNS) {
    if (fieldAt.has(col.key)) continue;
    const taken = new Set(fieldAt.values());
    const i = header.findIndex((h, idx) => !taken.has(idx) && col.aliases.includes(h));
    if (i >= 0) fieldAt.set(col.key, i);
  }
  if (!fieldAt.has('total') && !fieldAt.has('amount')) return { rows: [], missingHeader: true };
  const cell = (r: unknown[], f: ImportField): unknown => {
    const i = fieldAt.get(f);
    return i === undefined ? '' : r[i];
  };
  const text = (r: unknown[], f: ImportField): string => String(cell(r, f) ?? '').trim();
  const out: ImportRow[] = [];
  let lineNum = ctx.firstLineNum;
  for (let ri = headerIndex + 1; ri < rows.length; ri += 1) {
    const r = rows[ri];
    if (!r.some((c) => String(c ?? '').trim() !== '')) continue;
    const errors: string[] = [];
    const typeText = text(r, 'type').toLowerCase().replace(/[\s-]/g, '');
    const vatInvoice = text(r, 'vatInvoice');
    const normalInvoice = text(r, 'invoice');
    let lineType: LineType = typeText === 'vat' ? 'VAT' : typeText === 'nonvat' ? 'NonVAT' : (vatInvoice ? 'VAT' : 'NonVAT');
    if (lineType === 'VAT' && !ctx.vatRegistered) {
      errors.push('This company is not VAT-registered: VAT receipts are not used');
      lineType = 'NonVAT';
    }
    const line = emptyLine(lineNum, lineType, ctx.settings, ctx.vatCodes, ctx.vatRegistered);
    line.invoiceNo = (lineType === 'VAT' ? vatInvoice || normalInvoice : normalInvoice || vatInvoice).slice(0, 100);
    if (!line.invoiceNo) errors.push(lineType === 'VAT' ? 'VAT invoice no. is missing' : 'Invoice no. is missing');
    const date = parseSheetDate(cell(r, 'date'));
    if (cell(r, 'date') === '' || cell(r, 'date') === undefined) errors.push('Invoice date is missing');
    else if (!date) errors.push(`"${text(r, 'date')}" is not a date (use 26/10/2026)`);
    else line.invoiceDate = date;
    let vendorBy: ImportRow['vendorBy'] = null;
    let vendorKey = '';
    if (lineType === 'VAT') {
      vendorKey = text(r, 'vat');
      const fmt = checkVatFormat(vendorKey, ctx.vatCountry);
      if (!vendorKey) errors.push('Supplier VAT number is missing');
      else if (fmt.ok) {
        vendorBy = 'vat';
        line.supplierVat = fmt.digits;
      } else if (/^[A-Za-z0-9._-]{2,50}$/.test(vendorKey)) {
        vendorBy = 'code';
      } else {
        errors.push(fmt.message || 'The VAT number is not valid');
      }
      line.supplierName = text(r, 'supplier').slice(0, 200);
    } else {
      line.supplierName = (text(r, 'supplier') || text(r, 'vat')).slice(0, 200);
      if (!line.supplierName) errors.push('Supplier name is missing');
    }
    const amount = parseAmount(cell(r, 'amount'));
    const vatTyped = parseAmount(cell(r, 'vatAmount'));
    const total = parseAmount(cell(r, 'total'));
    if (text(r, 'amount') && amount === null) errors.push(`"${text(r, 'amount')}" is not an amount`);
    if (text(r, 'total') && total === null) errors.push(`"${text(r, 'total')}" is not an amount`);
    if (lineType === 'VAT') {
      const rate = line.vatRate;
      let before = amount;
      let vat = vatTyped;
      if (before === null && total !== null) {
        before = vat !== null ? round2(total - vat) : round2(total / (1 + rate / 100));
      }
      if (vat === null && before !== null) vat = total !== null ? round2(total - before) : vatOf(before, rate);
      line.amount = round2(before ?? 0);
      line.vatAmount = round2(vat ?? 0);
      line.total = round2(total ?? line.amount + line.vatAmount);
    } else {
      const value = total ?? amount;
      line.amount = round2(value ?? 0);
      line.vatAmount = 0;
      line.total = line.amount;
    }
    if (!(line.amount > 0)) errors.push('The amount is missing or not above 0');
    const categoryText = text(r, 'category');
    const category = ctx.categories.find((c) => c.isActive && c.name.trim().toLowerCase() === categoryText.toLowerCase());
    if (!categoryText) errors.push('Expense category is missing');
    else if (!category) errors.push(`No expense category is named "${categoryText}"`);
    else line.categoryId = category.categoryId;
    const description = text(r, 'description');
    line.description = description ? description.slice(0, 254) : null;
    out.push({ rowNumber: ri + 1, line, categoryText, vendorBy, vendorKey, errors });
    lineNum += 1;
  }
  return { rows: out, missingHeader: false };
}
